import { useRef, useState } from 'react';

import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { MultiSelect } from '@/components/atoms/MultiSelect';
import {
  SegmentedChip,
  SegmentedChipLabel,
  SegmentedChipPlaceholder,
  SegmentedChipRemove,
  SegmentedChipSegment,
} from '@/components/atoms/SegmentedChip/SegmentedChip';
import { CsatStarToggles } from '@/components/organisms/SessionsInfiniteTable/filters/CsatStarToggles';
import { FilterEmptyState } from '@/components/ui/custom/FilterEmptyState';
import {
  PopoverComboboxContent,
  PopoverComboboxGroup,
  PopoverComboboxItem,
  PopoverComboboxList,
} from '@/components/ui/custom/PopoverCombobox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { FilterType } from '@/modules/analytics/constants/metricsCatalog';
import {
  useFilterColumnOptions,
  type FilterColumnOption,
} from '@/modules/analytics/hooks/useFilterColumnOptions';
import { ALL_RATINGS } from '@/modules/analytics/utils/expandRatings';
import {
  type FilterChip as FilterChipModel,
  type FilterOperator,
  getOperatorsForType,
  isMultiValueOperator,
  isNoValueOperator,
  isRatingColumn,
  RATING_OPERATORS,
} from '@/modules/analytics/utils/reportFilters';
import { isKeyEnter } from '@/util/util';

type FilterChipProps = {
  column: string;
  dimensionLabel: string;
  chip: FilterChipModel;
  onChangeOperator: (operator: FilterOperator) => void;
  onChangeValues: (values: string[]) => void;
  onRemove: () => void;
  isValueOpen: boolean;
  onValueOpenChange: (open: boolean) => void;
};

const operatorI18nKey: Record<FilterOperator, string> = {
  is: 'analytics.reports_detail_filter_op_is',
  is_not: 'analytics.reports_detail_filter_op_is_not',
  is_any_of: 'analytics.reports_detail_filter_op_is_any_of',
  is_not_any_of: 'analytics.reports_detail_filter_op_is_not_any_of',
  is_set: 'analytics.reports_detail_filter_op_is_set',
  is_not_set: 'analytics.reports_detail_filter_op_is_not_set',
  contains: 'analytics.reports_detail_filter_op_contains',
  not_contains: 'analytics.reports_detail_filter_op_not_contains',
  starts_with: 'analytics.reports_detail_filter_op_starts_with',
  not_starts_with: 'analytics.reports_detail_filter_op_not_starts_with',
  ends_with: 'analytics.reports_detail_filter_op_ends_with',
  not_ends_with: 'analytics.reports_detail_filter_op_not_ends_with',
  gt: 'analytics.reports_detail_filter_op_gt',
  ge: 'analytics.reports_detail_filter_op_ge',
  lt: 'analytics.reports_detail_filter_op_lt',
  le: 'analytics.reports_detail_filter_op_le',
};

// For UUID-keyed enum columns (AI Agents, Environments) we collapse the
// operator menu to the set that matches a checkbox-list UX. Text-search
// operators are deliberately excluded — the stored value is an opaque UUID
// and substring matches against UUIDs would mislead users who expect to
// search against the label.
const ENUM_OPERATORS: FilterOperator[] = ['is_any_of', 'is_not_any_of', 'is_set', 'is_not_set'];

// For string-keyed enum columns (e.g. `channel`) text-search operators are
// meaningful — the stored value IS the searchable string. We expose the same
// substring/prefix/suffix ops a free-text string column would get, alongside
// the checkbox-list ops. The value editor switches based on the operator.
const STRING_ENUM_OPERATORS: FilterOperator[] = [
  'is_any_of',
  'is_not_any_of',
  'contains',
  'not_contains',
  'starts_with',
  'not_starts_with',
  'ends_with',
  'not_ends_with',
  'is_set',
  'is_not_set',
];

// Operators whose value editor is a free-text input even on enum-backed
// columns — checkbox-list pickers don't make sense for substring/prefix/
// suffix searches.
const TEXT_SEARCH_OPS: ReadonlySet<FilterOperator> = new Set([
  'contains',
  'not_contains',
  'starts_with',
  'not_starts_with',
  'ends_with',
  'not_ends_with',
]);

// String-typed columns whose stored value is an opaque enum code (e.g.
// goal_achieved stores '1'|'2'|'3', not the label). Same reasoning as the
// UUID case — substring/prefix matches against the code would mislead users
// who expect to search against the human-readable label.
const OPAQUE_CODED_ENUM_COLUMNS: ReadonlySet<string> = new Set(['goal_achieved', 'user_sentiment']);

// Resolves a stored chip value (e.g. a desk UUID) to its human label using the
// enum source. Falls back to the raw value when no match exists, so saved
// reports that reference a deleted entity still render something useful.
const labelForValue = (value: string, options: FilterColumnOption[] | null): string => {
  if (!options) {
    return value;
  }
  return options.find((o) => o.value === value)?.label ?? value;
};

// Joins values with commas for inline display in the chip. Long lists collapse
const valueSummary = (
  values: string[],
  type: FilterType,
  options: FilterColumnOption[] | null,
  t: TFunction
): string | null => {
  if (values.length === 0) {
    return null;
  }
  if (type === 'boolean') {
    const v = values[0];
    return t(
      v === 'true'
        ? 'analytics.reports_detail_filter_value_true'
        : 'analytics.reports_detail_filter_value_false'
    );
  }
  const labels = values.map((v) => labelForValue(v, options));
  if (labels.length <= 2) {
    return labels.join(', ');
  }
  return t('analytics.reports_detail_filter_value_count', { 0: labels.length });
};

const keepOpenOnFocusShift = (e: Event) => e.preventDefault();

type BooleanValueEditorProps = {
  values: string[];
  onCommit: (values: string[]) => void;
  onDone: () => void;
};

const BooleanValueEditor = ({ values, onCommit, onDone }: BooleanValueEditorProps) => {
  const { t } = useTranslation();
  const commandRef = useRef<HTMLDivElement>(null);
  const selected = values[0] === 'false' ? 'false' : 'true';
  const options = [
    { value: 'true', label: t('analytics.reports_detail_filter_value_true') },
    { value: 'false', label: t('analytics.reports_detail_filter_value_false') },
  ];

  return (
    <PopoverComboboxContent
      className="w-48"
      onFocusOutside={keepOpenOnFocusShift}
      onOpenAutoFocus={(e) => {
        e.preventDefault();
        commandRef.current?.focus();
      }}
      commandProps={{ ref: commandRef, defaultValue: selected }}
    >
      <PopoverComboboxList>
        <PopoverComboboxGroup>
          {options.map((opt) => (
            <PopoverComboboxItem
              key={opt.value}
              value={opt.value}
              selected={opt.value === selected}
              onSelect={() => {
                onCommit([opt.value]);
                onDone();
              }}
            >
              {opt.label}
            </PopoverComboboxItem>
          ))}
        </PopoverComboboxGroup>
      </PopoverComboboxList>
    </PopoverComboboxContent>
  );
};

// ── Free-text editor (numbers, free-text strings, opaque UUIDs) ─────────────

type FreeTextValueEditorProps = {
  type: FilterType;
  multi: boolean;
  values: string[];
  onCommit: (values: string[]) => void;
  onDone: () => void;
};

const splitMulti = (raw: string): string[] =>
  raw
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);

const initialFreeText = (values: string[], multi: boolean): string => {
  if (multi) {
    return values.join('\n');
  }
  return values[0] ?? '';
};

const getFreeTextPlaceholderKey = (type: FilterType, multi: boolean): string => {
  if (multi) {
    return 'analytics.reports_detail_filter_values_placeholder_text';
  }
  if (type === 'number') {
    return 'analytics.reports_detail_filter_values_placeholder_number';
  }
  return 'analytics.reports_detail_filter_value_placeholder_single';
};

const FreeTextValueEditor = ({
  type,
  multi,
  values,
  onCommit,
  onDone,
}: FreeTextValueEditorProps) => {
  const { t } = useTranslation();
  // Local input state for typing; we don't mirror `values` via effect — the
  // parent re-keys this component when external values change, which resets
  // the local state cleanly.
  const [draft, setDraft] = useState(() => initialFreeText(values, multi));
  const placeholder = t(getFreeTextPlaceholderKey(type, multi));
  const commitSingle = () => onCommit(draft.trim() ? [draft.trim()] : []);

  return (
    <PopoverContent
      align="start"
      className="flex w-64 flex-col gap-1 p-3"
      onFocusOutside={keepOpenOnFocusShift}
    >
      {multi ? (
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => onCommit(splitMulti(draft))}
          placeholder={placeholder}
          rows={4}
          autoFocus
        />
      ) : (
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitSingle}
          onKeyDown={(e) => {
            if (isKeyEnter(e)) {
              e.preventDefault();
              commitSingle();
              onDone();
            }
          }}
          placeholder={placeholder}
          autoFocus
        />
      )}
      {multi && (
        <p className="text-xs leading-4 text-foreground-muted">
          {t('analytics.reports_detail_filter_values_hint')}
        </p>
      )}
    </PopoverContent>
  );
};

type RatingValueEditorProps = {
  operator: FilterOperator;
  values: string[];
  onCommit: (values: string[]) => void;
  onDone: () => void;
};

const RatingValueEditor = ({ operator, values, onCommit, onDone }: RatingValueEditorProps) => {
  const selected = values.map(Number).filter(Number.isFinite);
  const isThreshold = operator === 'gt' || operator === 'lt';

  const handleToggle = (star: number) => {
    if (isThreshold) {
      onCommit([String(star)]);
      onDone();
      return;
    }
    const next = selected.includes(star)
      ? selected.filter((s) => s !== star)
      : [...selected, star].sort((a, b) => a - b);
    onCommit(next.map(String));
  };

  const isDisabled = (star: number) =>
    (operator === 'gt' && star === ALL_RATINGS.at(-1)) ||
    (operator === 'lt' && star === ALL_RATINGS[0]);

  return (
    <PopoverContent align="start" className="w-auto p-3" onFocusOutside={keepOpenOnFocusShift}>
      <CsatStarToggles selected={selected} onToggle={handleToggle} isDisabled={isDisabled} />
    </PopoverContent>
  );
};

// ── Dispatcher ───────────────────────────────────────────────────────────────

type ValueEditorProps = {
  column: string;
  type: FilterType;
  operator: FilterOperator;
  values: string[];
  onCommit: (values: string[]) => void;
  onDone: () => void;
};

const ValueEditor = ({ column, type, operator, values, onCommit, onDone }: ValueEditorProps) => {
  if (isRatingColumn(column) && RATING_OPERATORS.includes(operator)) {
    return (
      <RatingValueEditor operator={operator} values={values} onCommit={onCommit} onDone={onDone} />
    );
  }
  if (type === 'boolean') {
    return <BooleanValueEditor values={values} onCommit={onCommit} onDone={onDone} />;
  }
  // Re-key on operator change so the free-text input resets between
  // single-value and multi-value modes instead of stale-mirroring.
  return (
    <FreeTextValueEditor
      key={operator}
      type={type}
      multi={isMultiValueOperator(operator)}
      values={values}
      onCommit={onCommit}
      onDone={onDone}
    />
  );
};

const getChipOperators = (
  column: string,
  type: FilterType,
  isEnumBacked: boolean
): FilterOperator[] => {
  if (isRatingColumn(column)) {
    return RATING_OPERATORS;
  }
  if (!isEnumBacked) {
    return getOperatorsForType(type);
  }
  if (OPAQUE_CODED_ENUM_COLUMNS.has(column)) {
    return ENUM_OPERATORS;
  }
  return type === 'string' ? STRING_ENUM_OPERATORS : ENUM_OPERATORS;
};

export const FilterChip = ({
  column,
  dimensionLabel,
  chip,
  onChangeOperator,
  onChangeValues,
  onRemove,
  isValueOpen,
  onValueOpenChange,
}: FilterChipProps) => {
  const { t } = useTranslation();

  const { options } = useFilterColumnOptions(column);
  const isEnumBacked = options !== null;

  const noValue = isNoValueOperator(chip.operator);
  // Enum-backed columns get a restricted operator menu so the UX (checkbox
  // list with possibly many items) lines up with what the operator actually
  // means. String-valued enums (channels) additionally allow text-search ops
  // because the stored value is itself searchable; UUID-valued enums (AI
  // agents, environments) stick to the checkbox-list ops only.
  const operators = getChipOperators(column, chip.type, isEnumBacked);

  const getOperatorLabel = (op: FilterOperator) =>
    isRatingColumn(column) && op === 'is_any_of'
      ? t('analytics.reports_detail_filter_op_is')
      : t(operatorI18nKey[op]);
  const usesMultiSelect = !!options && !TEXT_SEARCH_OPS.has(chip.operator);
  const summary = valueSummary(chip.values, chip.type, options, t);
  const valueSegment = (
    <SegmentedChipSegment variant="value">
      {summary ?? (
        <SegmentedChipPlaceholder>
          {t('analytics.reports_detail_filter_value_empty')}
        </SegmentedChipPlaceholder>
      )}
    </SegmentedChipSegment>
  );

  return (
    <SegmentedChip>
      <SegmentedChipLabel>{dimensionLabel}</SegmentedChipLabel>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <SegmentedChipSegment variant="comparison">
            {getOperatorLabel(chip.operator)}
          </SegmentedChipSegment>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-44">
          {operators.map((op) => (
            <DropdownMenuItem
              key={op}
              className={cn(op === chip.operator && 'bg-highlight-background')}
              onSelect={() => onChangeOperator(op)}
            >
              {getOperatorLabel(op)}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {!noValue && usesMultiSelect && (
        <MultiSelect
          trigger={valueSegment}
          options={options ?? []}
          selectedValues={chip.values}
          onSelectionChange={onChangeValues}
          showSelectAll
          sortOptions={false}
          placeholder={t('common.search_dots')}
          popoverClassName="w-64"
          open={isValueOpen}
          onOpenChange={onValueOpenChange}
          onFocusOutside={keepOpenOnFocusShift}
          emptyState={<FilterEmptyState text={t('common.no_results')} />}
        />
      )}
      {!noValue && !usesMultiSelect && (
        <Popover open={isValueOpen} onOpenChange={onValueOpenChange}>
          <PopoverTrigger asChild>{valueSegment}</PopoverTrigger>
          <ValueEditor
            column={column}
            type={chip.type}
            operator={chip.operator}
            values={chip.values}
            onCommit={onChangeValues}
            onDone={() => onValueOpenChange(false)}
          />
        </Popover>
      )}
      <SegmentedChipRemove label={dimensionLabel} onClick={onRemove} />
    </SegmentedChip>
  );
};
